import _ from "lodash";
import { CONTENT_AGING, CONTENT_DUPLICATION, CONTENT_EXPIRY, CONTENT_MAP, UNSTRUCTURED_CONTENT } from "src/constants/adoption/sourceTypes";

interface ScoreCardFilter {
  name: string,
  value: any,
  subFilters: ScoreCardFilter[] | null,
}

export interface ScoreCard {
  key: string;
  title: string;
  count: number;
  countLabel: string;
  icon: string;
  description: string;
  note?: HTMLElement;
  value: string;
  filters: ScoreCardFilter[];
}

export const getGraphValues = (
  data: any,
  selectedCard: ScoreCard,
  selectedFilter: string,
) => {

  let graphValues: { name: string; value: number }[] = [];
  switch (selectedCard.key) {
    case "CONTENT_AGING": {
      const {
        sixToNineMonthsCount,
        nineToTwelveMonthsCount,
        twelveToEighteenMonthsCount,
        moreThanEighteenMonthsCount,
      } = data;

      graphValues = [
        { name: "6m - 9m", value: sixToNineMonthsCount || 0 },
        { name: "9m - 12m", value: nineToTwelveMonthsCount || 0 },
        { name: "1y - 1.5y", value: twelveToEighteenMonthsCount || 0 },
        { name: "1.5+ y", value: moreThanEighteenMonthsCount || 0 },
      ];
      break;
    }

    case "UNSTRUCTURED_CONTENT":
      {
        const {
          lacksCategoriesCount,
          lacksDescriptionsCount,
          lacksTagsCount,
          noContextCount,
        } = data;

        graphValues = [
          { name: "Lacks Descriptions", value: lacksDescriptionsCount || 0 },
          { name: "Lacks Tags", value: lacksTagsCount || 0 },
          { name: "Lacks Categories", value: lacksCategoriesCount || 0 },
          { name: "No Context", value: noContextCount || 0 },
        ];
      }
      break;

    case "CONTENT_DUPLICATION":
      {
        const { duplicateAssetCount, originalAssetCount } = data;

        graphValues = [
          { name: "Duplicate Assets", value: duplicateAssetCount || 0 },
          { name: "Original Assets", value: originalAssetCount || 0 },
        ];
      }
      break;

    case "CONTENT_EXPIRY":
      {
        const {
          expiredMoreThanOneMonth,
          expiredMoreThanSixMonths,
          expiredMoreThanThreeMonths,
          expiredThisMonth,
          expiringToday,
          expiringThisWeek,
          expiringThisMonth,
          expiringMoreThanOneMonth,
        } = data;

        if (selectedFilter === "ALL_EXPIRED") {
          graphValues = [
            { name: "0 - 1m", value: expiredThisMonth || 0 },
            { name: "1m - 3m", value: expiredMoreThanOneMonth || 0 },
            {
              name: "3m - 6m",
              value: expiredMoreThanThreeMonths || 0,
            },
            {
              name: "6+ m",
              value: expiredMoreThanSixMonths || 0,
            },
          ];
        } else {
          graphValues = [
            { name: "0 - 1d", value: expiringToday || 0 },
            { name: "0 - 7d", value: expiringThisWeek || 0 },
            { name: "0 - 30d", value: expiringThisMonth || 0 },
            {
              name: "30+ d",
              value: expiringMoreThanOneMonth || 0,
            },
          ];
        }
      }
      break;
    default:
      break;
  }

  return graphValues;
};

export const getContentHealthScoreCard = (scoreData: any) => [
    {
      key: "CONTENT_AGING",
      title: "content aging",
      count: _.get(scoreData, "contentAgingCount", 0),
      countLabel: "stale assets",
      icon: "paperflite-content-age",
      description: `<p style="font-weight: 500"}>Fresh content = better sales conversations.</p> <p>These assets haven't been updated in 6+ months and may no longer reflect your current messaging or offerings. Review and refresh to keep your sales team confident and current.</p>`,
      value: "ALL",
      filters: [
        { name: "All", value: "ALL" },
        { name: "6 - 9 months", value: "SIX_TO_NINE_MONTHS" },
        { name: "9 - 12 months", value: "NINE_TO_TWELVE_MONTHS" },
        { name: "1 - 1.5 years", value: "TWELVE_TO_EIGHTEEN_MONTHS" },
        { name: "1.5+ years", value: "MORE_THAN_EIGHTEEN_MONTHS" },
      ],
    },
    {
      key: "UNSTRUCTURED_CONTENT",
      title: "content organization",
      count: _.get(scoreData, "unstructuredContentCount", 0),
      countLabel: "uncategorized assets",
      icon: "paperflite-shapes",
      description: `<p style="font-weight: 500">Well-organized content gets used. Messy content gets ignored.</p> <p>These assets are missing tags, categories, or descriptions—making them nearly invisible to your sales team. Add the missing details so the right content surfaces at the right moment.</p>`,
      note: `Note: No categories found. Set them up in <a href="/settings/custom_fields" target="_blank">custom fields</a>.`,
      value: "ALL",
      filters: [
        { name: "All", value: "ALL" },
        { name: "Lacks description", value: "LACKS_DESCRIPTIONS" },
        { name: "Lacks tags", value: "LACKS_TAGS" },
        { name: "Lacks categories", value: "LACKS_CATEGORIES" },
        { name: "No context", value: "NO_CONTEXT" },
      ],
    },
    {
      key: "CONTENT_DUPLICATION",
      title: "content duplication",
      count: _.get(scoreData, "contentDuplicationCount", 0),
      countLabel: "duplicate assets",
      icon: "paperflite-duplicates",
      description: `<p style="font-weight: 500">Duplicates create confusion and muddy your metrics.</p> <p>Multiple versions of the same content frustrate users and make it impossible to track what's actually working. Clean up duplicates to streamline your hub and sharpen your insights.</p>`,
      value: "DUPLICATE",
      filters: [],
    },
    {
      key: "CONTENT_EXPIRY",
      title: "content expiry",
      count: _.get(scoreData, "contentExpiryCount", 0),
      countLabel: "expired assets",
      icon: "paperflite-timer",
      description:`<p style="font-weight: 500">Expired content can damage credibility in sales conversations.</p> <p>Keep your team confident by extending expiry dates for relevant content, uploading newer versions, or removing what's no longer useful. </p>`,
      value: "ALL_EXPIRED",
      filters: [
        {
          name: "All expired",
          value: "ALL_EXPIRED",
          subFilters: [
            { name: "All expired", value: "ALL_EXPIRED" },
            { name: "Expired this month", value: "PAST_MONTH" },
            { name: "Expired >1 month", value: "ONE_TO_THREE_MONTHS" },
            { name: "Expired >3 months", value: "THREE_TO_SIX_MONTHS" },
            { name: "Expired >6 months", value: "MORE_THAN_SIX_MONTHS" },
          ],
        },
        {
          name: "All expiring",
          value: "ALL_EXPIRING",
          subFilters: [
            {name: "All expiring", value: "ALL_EXPIRING"},
            { name: "Expiring today", value: "EXPIRING_TODAY" },
            { name: "Expiring in 7 days", value: "EXPIRING_THIS_WEEK" },
            { name: "Expiring in 30 days", value: "EXPIRING_THIS_MONTH" },
            { name: "Expiring later", value: "EXPIRING_LATER" },
          ],
        },
      ],
    },
  ];

export const getSectionType = (section: string) => {

  switch (section) {
    case "CONTENT_AGING":
      return CONTENT_AGING;

    case "UNSTRUCTURED_CONTENT":
      return UNSTRUCTURED_CONTENT;

    case "CONTENT_DUPLICATION":
      return CONTENT_DUPLICATION;

    case "CONTENT_EXPIRY":
      return CONTENT_EXPIRY;
      
    default:
      return CONTENT_MAP;
  }
}
